import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Globe, Bell, ChevronRight, Info, Shield, UserPlus, X, Send, Crown, BookOpen } from 'lucide-react';
import AppGuideModal from '../../components/common/AppGuideModal.jsx';
import logo from '../../assets/logo.png';
import styles from './SettingsPage.module.css';
import { useUserStore } from '../../store/useUserStore.js';
import { IS_FREE_LAUNCH_PHASE } from '../../config.js';

const LANGUAGES = [
  { code: 'ko', label: '한국어', flag: '🇰🇷' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'zh', label: '中文(简体)', flag: '🇨🇳' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
];

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.substring(0, 2);
  
  const isPremium = useUserStore(state => state.isPremium);
  const purchasePremium = useUserStore(state => state.purchasePremium);

  function changeLanguage(lang) {
    i18n.changeLanguage(lang);
  }

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [invitePhone, setInvitePhone] = useState('');

  const handleImportContact = async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        const { Contacts } = await import('@capacitor-community/contacts');
        const result = await Contacts.pickContact({
          projection: { name: false, phones: true }
        });
        if (result && result.contact) {
          const phone = result.contact.phones?.[0]?.number || '';
          setInvitePhone(phone);
        }
      } catch (error) {
        console.log('Failed to pick contact', error);
      }
      return;
    }

    if ('contacts' in navigator) {
      try {
        const props = ['name', 'tel'];
        const opts = { multiple: false };
        const contacts = await navigator.contacts.select(props, opts);
        if (contacts && contacts.length > 0) {
          const phone = contacts[0].tel?.[0] || '';
          setInvitePhone(phone);
        }
      } catch (error) {
        console.log('Web Contacts API failed', error);
      }
      return;
    }

    alert('스마트폰 주소록 연동은 네이티브 앱이거나 지원되는 모바일 브라우저에서만 가능합니다.');
  };

  const handleSendInvite = () => {
    if (!invitePhone) return;
    const body = encodeURIComponent(t('settings.inviteMessage'));
    window.location.href = `sms:${invitePhone}?body=${body}`;
    setShowInviteModal(false);
    setInviteName('');
    setInvitePhone('');
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{t('settings.title')}</h1>
      </div>

      <div className={styles.content}>
        {/* 언어 설정 */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Globe size={16} color="var(--color-sky)" />
            <h2 className={styles.sectionTitle}>{t('settings.language')}</h2>
          </div>
          <div className={styles.langOptions}>
            {LANGUAGES.map(({ code, label, flag }) => (
              <button
                key={code}
                id={`lang-${code}`}
                className={`${styles.langBtn} ${currentLang === code ? styles.langActive : ''}`}
                onClick={() => changeLanguage(code)}
              >
                <span className={styles.flag}>{flag}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 알림 설정 및 안전 스위치 (기능 미구현으로 임시 주석 처리)
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Bell size={16} color="var(--color-sky)" />
            <h2 className={styles.sectionTitle}>{t('settings.notification')}</h2>
          </div>
          <div className={styles.settingList}>
            <SettingRow label={t('settings.congestionAlert')} value={t('settings.on')} />
            <SettingRow label={t('settings.radius')} value={t('settings.valueDistance')} />
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Shield size={16} color="var(--color-sky)" />
            <h2 className={styles.sectionTitle}>{t('deadman.title')}</h2>
          </div>
          <div className={styles.settingList}>
            <SettingRow label={t('deadman.threshold')} value={t('settings.valueTime')} />
            <SettingRow label={t('deadman.contacts')} value={t('settings.valuePersons')} />
          </div>
        </div>
        */}

        {/* 친구 초대 */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <UserPlus size={16} color="var(--color-sky)" />
            <h2 className={styles.sectionTitle}>{t('settings.inviteTitle')}</h2>
          </div>
          <div className={styles.settingList}>
            <SettingRow label={t('settings.inviteMenu')} hasChevron onClick={() => setShowInviteModal(true)} />
          </div>
        </div>

        {/* 프리미엄 혜택 */}
        {!IS_FREE_LAUNCH_PHASE && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <Crown size={16} color="#f59e0b" />
              <h2 className={styles.sectionTitle}>프리미엄 혜택</h2>
            </div>
            <div className={styles.settingList}>
              <SettingRow 
                label={isPremium ? '👑 프리미엄 이용 중' : '👑 프리미엄 업그레이드 (광고 제거)'} 
                hasChevron={!isPremium} 
                onClick={!isPremium ? () => {
                  purchasePremium();
                  alert('프리미엄 결제가 완료되었습니다! 배너 광고가 제거되고 자유롭게 그룹 생성이 가능합니다.');
                } : undefined} 
              />
            </div>
          </div>
        )}

        {/* 앱 사용법 */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <BookOpen size={16} color="var(--color-sky)" />
            <h2 className={styles.sectionTitle}>앱 사용법</h2>
          </div>
          <div className={styles.settingList}>
            <SettingRow label="앱 사용 가이드" hasChevron onClick={() => setShowGuide(true)} />
          </div>
        </div>

        {/* 앱 정보 */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Info size={16} color="var(--color-sky)" />
            <h2 className={styles.sectionTitle}>{t('settings.about')}</h2>
          </div>
          <div className={styles.settingList}>
            <SettingRow label={t('settings.version')} value={t('settings.valueBeta')} />
            <SettingRow label={t('settings.privacy')} hasChevron onClick={() => window.open('https://www.notion.so/368d79df16dc807bb78cff93a9ff59e3?source=copy_link', '_blank')} />
            <SettingRow label={t('settings.terms')} hasChevron onClick={() => window.open('https://www.notion.so/368d79df16dc807bb78cff93a9ff59e3?source=copy_link', '_blank')} />
          </div>
        </div>

        <div className={styles.appInfo}>
          <img src={logo} alt="Logo" className={styles.appLogoImg} />
          <p className={styles.appName}>{t('settings.appTitle')}</p>
          <p className={styles.appSlogan}>{t('slogan')}</p>
        </div>
      </div>

      {/* 앱 사용법 모달 */}
      {showGuide && <AppGuideModal onClose={() => setShowGuide(false)} />}

      {/* 친구 초대 모달 */}
      {showInviteModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className="flex justify-between items-center mb-4">
              <h3>{t('settings.inviteModalTitle')}</h3>
              <button onClick={() => setShowInviteModal(false)} aria-label="모달 닫기"><X size={20}/></button>
            </div>
            <div className={styles.contactForm}>
              <button 
                className={`btn btn-ghost`} 
                onClick={handleImportContact}
                style={{ width: '100%', marginBottom: '16px', border: '1px dashed var(--color-sky)', color: 'var(--color-navy)', backgroundColor: 'var(--color-primary-50)' }}
              >
                <UserPlus size={16} style={{ marginRight: '8px' }} color="var(--color-navy)" /> 
                {t('importContacts')}
              </button>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '4px' }}>전화번호 (필수)</label>
                <input 
                  type="tel" 
                  className={styles.modalInput} 
                  placeholder="숫자만 입력 (예: 01012345678)"
                  value={invitePhone}
                  onChange={(e) => setInvitePhone(e.target.value)}
                />
              </div>
              <button 
                className="btn btn-primary"
                style={{ width: '100%' }}
                onClick={handleSendInvite}
                disabled={!invitePhone}
              >
                <Send size={16} className="inline mr-2" />
                {t('settings.inviteSendBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingRow({ label, value, hasChevron, onClick }) {
  return (
    <div 
      className={styles.settingRow} 
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      <span className={styles.settingLabel}>{label}</span>
      <div className={styles.settingRight}>
        {value && <span className={styles.settingValue}>{value}</span>}
        {hasChevron && <ChevronRight size={16} color="var(--color-text-muted)" />}
      </div>
    </div>
  );
}
